import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { MatterActivityFeedActions } from '../store/slice'
import {
  selectMatterActivityError,
  selectMatterActivityHasMore,
  selectMatterActivityInitialLoadComplete,
  selectMatterActivityLoading,
  selectMatterActivityRows,
  selectMatterActivityStale,
} from '../store/selectors'
import { ActivityEventType, type ActivityFeedRow } from '../schemas'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'

const kInitialRevealCount = 12
const kRevealIncrement = 12

export type UseActivityFeedResult = {
  /** The rows revealed so far, which Load more extends. */
  rows: ActivityFeedRow[]
  /** True while a matter's activity can't be shown yet: loading, with nothing cached to fall back on. */
  isLoadingWithoutCachedRows: boolean
  isError: boolean
  canRevealMore: boolean
  /**
   * True at the bottom of an expanded list holding everything the matter has, so the feed can say so.
   * A list that fits in the first batch stays quiet: reaching its end took no effort worth confirming.
   */
  isEndOfActivity: boolean
  revealMore: () => void
  refetch: () => void
}

/** Provides a way to access a matter's activity feed. Mount it per matter: the reveal depth is its own. */
export function useActivityFeed(matterId: string): UseActivityFeedResult {
  const dispatch = useAppDispatch()

  const allRows = useAppSelector((state: RootState) => selectMatterActivityRows(state, matterId))
  const { enabled, loading } = useRuntimeFeatureFlag(RuntimeFeatureFlag.TABLES)
  const rows =
    enabled && !loading ? allRows : allRows.filter((row) => row.event !== ActivityEventType.TableCreated && row.event !== ActivityEventType.TableUpdated)
  const hasMoreBeyondFeed = useAppSelector((state: RootState) => selectMatterActivityHasMore(state, matterId))
  const isLoading = useAppSelector((state: RootState) => selectMatterActivityLoading(state, matterId))
  const isError = useAppSelector((state: RootState) => selectMatterActivityError(state, matterId))
  const initialLoadComplete = useAppSelector((state: RootState) => selectMatterActivityInitialLoadComplete(state, matterId))
  const isStale = useAppSelector((state: RootState) => selectMatterActivityStale(state, matterId))
  const [revealCount, setRevealCount] = useState(kInitialRevealCount)

  // Fetch on mount and matter change, and again when the rows go stale while mounted (isStale), so the
  // feed updates live. The listener no-ops if the cache is still fresh.
  useEffect(() => {
    dispatch(MatterActivityFeedActions.refreshMatterActivity({ matterId, forceRefresh: false }))
  }, [matterId, isStale, dispatch])

  const revealMore = useCallback(() => setRevealCount((count) => count + kRevealIncrement), [])

  const refetch = useCallback(() => {
    dispatch(MatterActivityFeedActions.refreshMatterActivity({ matterId, forceRefresh: true }))
  }, [matterId, dispatch])

  const canRevealMore = rows.length > revealCount
  return {
    rows: rows.slice(0, revealCount),
    // A matter nothing has fetched yet is still loading; only a completed fetch can call it empty.
    isLoadingWithoutCachedRows: rows.length === 0 && !isError && (isLoading || !initialLoadComplete),
    isError,
    canRevealMore,
    isEndOfActivity: rows.length > kInitialRevealCount && !canRevealMore && !hasMoreBeyondFeed,
    revealMore,
    refetch,
  }
}
